import { useState } from "react";
import {
  Save,
  Shield,
  Puzzle,
  HelpCircle,
  Users,
  Settings2,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { Label } from "@/components/ui/label";
import PasswordChangeForm from "./components/change-password";

export const description =
  "A modern settings page with tabs navigation and responsive design for managing various account settings.";

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState("general");

  return (
    <div className="flex min-h-screen w-full flex-col">
      <main className="flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 bg-background p-4 md:gap-6 md:p-8">
        <div className="mx-auto w-full max-w-5xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your account settings and preferences
              </p>
            </div>
            <Button size="sm" variant="outline">
              <Settings2 className="mr-2 h-4 w-4" />
              Reset Defaults
            </Button>
          </div>

          <Separator className="my-6" />

          <Tabs
            defaultValue="general"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="border-b">
              <TabsList className="flex w-full bg-transparent border-b-0 h-full justify-start px-4 pt-2">
                <TabsTrigger
                  value="general"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <Save className="h-4 w-4" />
                  General
                </TabsTrigger>
                <TabsTrigger
                  value="security"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <Shield className="h-4 w-4" />
                  Security
                </TabsTrigger>
                <TabsTrigger
                  value="integrations"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <Puzzle className="h-4 w-4" />
                  Integrations
                </TabsTrigger>
                <TabsTrigger
                  value="support"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <HelpCircle className="h-4 w-4" />
                  Support
                </TabsTrigger>
                <TabsTrigger
                  value="organizations"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <Users className="h-4 w-4" />
                  Organizations
                </TabsTrigger>
                <TabsTrigger
                  value="advanced"
                  className="flex items-center gap-2 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <Settings2 className="h-4 w-4" />
                  Advanced
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="mt-6">
              <TabsContent value="general" className="space-y-6">
                <Card className="overflow-hidden border bg-card shadow-sm">
                  <CardHeader className="bg-muted/40">
                    <CardTitle className="flex items-center text-lg">
                      Company Information
                      <Badge variant="outline" className="ml-2">
                        Required
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Customize how your company appears in the marketplace
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="company-name">Company Name</Label>
                        <Input
                          id="company-name"
                          placeholder="Enter your company name"
                        />
                        <p className="text-xs text-muted-foreground">
                          This name will be visible to customers and in search
                          results
                        </p>
                      </div>
                    </form>
                  </CardContent>
                  <CardFooter className="bg-muted/20 border-t px-6 py-4 flex justify-between">
                    <Button variant="outline">Cancel</Button>
                    <Button>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>

              <TabsContent value="security" className="space-y-6">
                <Card className="overflow-hidden border bg-card shadow-sm">
                  <CardHeader className="bg-muted/40">
                    <CardTitle className="text-lg">Security Settings</CardTitle>
                    <CardDescription>
                      Manage your password and account security preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <PasswordChangeForm />
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
